import { getElementBoundaryById } from "common/utils/dom";
import { camelCaseToTitleCase } from "common/utils/strings";
import { StudioNoSearchResultsPlaceholderMessage } from "lib/components/studio/StudioPlaceholderMessage";
import { StudioBrowserTabs } from "lib/studio/studioConstants";
import type { CSSProperties, FocusEvent, ReactElement, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import Link from "ui/components/Link";
import cx from "ui/cx";
import ExpandableSelect from "ui/forms/ExpandableSelect";
import type { OptionProps, SelectProps } from "ui/forms/Select";
import useOutsideClick from "ui/hooks/useOutsideClick";
import IconLineArrow from "ui/icons/IconLineArrow";
import IconQuestionMark from "ui/icons/IconQuestionMark";
import type { StyledComponentProps } from "ui/oldStyles/styledComponentProps";
import type { SearchInputProps } from "ui/search/SearchInput";
import SearchInput from "ui/search/SearchInput";
import ContextMenu from "../ContextMenu";
import StudioTooltip from "../StudioTooltip";
import SubmenuInnerButton from "../SubmenuInnerButton";
import styles from "./InspectorComponents.module.scss";
import type { ReturnCommitNumberInputProps } from "./ReturnCommitNumberInput";
import type { ReturnCommitTextInputProps } from "./ReturnCommitTextInput";
import { STUDIO_FLYOUT_MENU_ID } from "./StudioFlyoutMenu";

const SUBMENU_LEAVE_CLOSE_DELAY_MS = 200;

interface FloatingInspectorSelectProps extends InspectorSelectProps<string | number | boolean> {
  open: boolean;
  onClose: (() => void) | undefined;
  position?: "left" | "right";
  minWidth?: number;
}

export function FloatingInspectorSelect({
  open,
  onClose,
  position = "right",
  className,
  minWidth,
  ...selectProps
}: FloatingInspectorSelectProps) {
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, onClose);

  if (!open) {
    return null;
  }

  return (
    <div
      className={cx(
        styles,
        styles.floatingDropdownMenuWrapper,
        {
          [styles.floatingDropdownPositionLeft]: position === "left",
        },
        className,
      )}
      ref={ref}
    >
      <InspectorSelect {...selectProps} />
    </div>
  );
}

interface InspectorToggleButton {
  selected: boolean;
  label?: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}

interface InspectorToggleButtonsProps {
  disabled?: boolean;
  left: InspectorToggleButton;
  right: InspectorToggleButton;
}

export function InspectorToggleButtons({ disabled, left, right }: InspectorToggleButtonsProps) {
  return (
    <div className={styles.inspectorToggleButtonsWrapper}>
      <button
        type="button"
        title={left.title}
        disabled={disabled || left.disabled}
        className={cx(left.selected && styles.inspectorToggleButtonSelected)}
        onClick={left.onClick}
      >
        {left.icon}
        {left.label}
      </button>
      <button
        type="button"
        title={right.title}
        disabled={disabled || right.disabled}
        className={cx(right.selected && styles.inspectorToggleButtonSelected)}
        onClick={right.onClick}
      >
        {right.icon}
        {right.label}
      </button>
    </div>
  );
}

interface InspectorNoteProps {
  children: ReactNode;
  size?: "base" | "small" | "xsmall";
  weight?: "medium";
  padded?: boolean;
  centered?: boolean;
}

interface InspectorValidationErrorProps {
  children: ReactNode;
  inline?: boolean;
}

export function InspectorValidationError({
  children,
  inline = false,
}: InspectorValidationErrorProps): ReactElement | null {
  if (!children) {
    return null;
  }

  const className = cx(styles.inspectorValidationError, inline && styles.inspectorValidationErrorInline);
  if (inline) {
    return <span className={className}>{children}</span>;
  }

  return <div className={className}>{children}</div>;
}

export function InspectorValidationWarning({
  children,
  inline = false,
}: InspectorValidationErrorProps): ReactElement | null {
  if (!children) {
    return null;
  }

  const className = cx(styles.inspectorValidationWarning, inline && styles.inspectorValidationWarningInline);
  if (inline) {
    return <span className={className}>{children}</span>;
  }

  return <div className={className}>{children}</div>;
}

export function InspectorNote({ children, size = "small", weight, padded, centered = true }: InspectorNoteProps) {
  return (
    <div
      className={cx(styles.inspectorNote, {
        [styles.noteSizeBase]: size === "base",
        [styles.noteSizeSmall]: size === "small",
        [styles.noteSizeXSmall]: size === "xsmall",
        [styles.noteWeightMedium]: weight === "medium",
        [styles.notePadded]: padded,
        [styles.noteCentered]: centered,
      })}
    >
      {children}
    </div>
  );
}

interface EntityListProps extends Omit<StyledComponentProps, "children">, Partial<SearchInputProps> {
  searchable?: boolean;
  resultsEmpty?: boolean;
  search?: string;
  omitPaddingLeftRight?: boolean;
}

export function EntityList({
  children,
  style,
  className,
  searchable = false,
  resultsEmpty = false,
  search,
  omitPaddingLeftRight = false,
  ...searchInputProps
}: EntityListProps) {
  return (
    <div
      style={style}
      className={cx(styles.entityList, { [styles.omitPaddingLeftRight]: omitPaddingLeftRight }, className)}
    >
      {searchable && (
        <div className={styles.searchWrapper}>
          <SearchInput
            inputSize="medium"
            clearSearch={searchInputProps.clearSearch ?? (() => {})}
            {...searchInputProps}
          />
        </div>
      )}
      {resultsEmpty && <StudioNoSearchResultsPlaceholderMessage search={search ?? ""} />}
      {children}
    </div>
  );
}

export function ThinFlexRow({ children, style }: StyledComponentProps) {
  return (
    <div className={styles.thinFlexRow} style={style}>
      {children}
    </div>
  );
}

export function InspectorClearableRotationControlsWrapper({ children, style }: StyledComponentProps) {
  return (
    <div className={styles.clearableRotationControls} style={style}>
      {children}
    </div>
  );
}

export const SectionInlineTitle = ({ title }: { title: string }) => (
  <span className={styles.sectionTitleSpan}>{camelCaseToTitleCase(title)}</span>
);

export interface InspectorSelectProps<T extends number | string | boolean>
  extends Pick<
    SelectProps,
    | "disabled"
    | "placeholder"
    | "openImmediately"
    | "clearOnChange"
    | "persistOnChange"
    | "additiveOption"
    | "mouseEvent"
    | "className"
    | "hideInput"
    | "inputFormatter"
    | "optionsClassName"
    | "showTooltip"
    | "tooltipText"
    | "tooltipDelay"
  > {
  onSubmenuOpen?: SelectProps["onSubmenuOpen"];
  values?: (T | undefined | null)[];
  options?: SelectProps["options"];
  value: T | null;
  sortValues?: boolean;
  renamer?: (x: string) => string | undefined;
  onChange: ((s: string) => void) | undefined;
  label?: string;
  labelClassName?: string;
  ref?: RefObject<HTMLDivElement>;
  withinFlyout?: boolean;
}

export const InspectorSelect = <T extends number | string | boolean>({
  value,
  values,
  ref,
  options: optionsProp,
  renamer,
  sortValues,
  onChange,
  placeholder,
  disabled,
  label,
  labelClassName,
  openImmediately = false,
  clearOnChange = false,
  persistOnChange = false,
  additiveOption,
  mouseEvent = "onMouseDown",
  className,
  hideInput,
  withinFlyout = false,
  inputFormatter,
  optionsClassName,
  showTooltip = false,
  tooltipText,
  tooltipDelay,
  onSubmenuOpen: onSubmenuOpenProp,
}: InspectorSelectProps<T>) => {
  console.assert(
    (optionsProp !== undefined) !== (values !== undefined && values !== null),
    "Exactly one parameter of options/values should be defined.",
  );

  const [submenuState, setSubmenuState] = useState<{
    anchorRect: DOMRect;
    subOptions: OptionProps[];
    onSelect: (value: string | number | boolean | null) => void;
  } | null>(null);
  const submenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const rename = (x?: string | number | boolean | null) => {
    if (renamer) {
      return renamer(x?.toString() ?? "") ?? "";
    }

    return x?.toString() ?? "";
  };

  let options = optionsProp;
  if (values !== undefined && values !== null) {
    const optionNamePairs: [T, string][] = values.map((x) => [x, rename(x)] as [T, string]);

    if (sortValues) {
      optionNamePairs.sort((a, b) => a[1].localeCompare(b[1]));
    }
    options = optionNamePairs.map((o) => ({ label: o[1], value: o[0] }));
  }

  const hasSubmenuOptions =
    Array.isArray(options) && options.some((o) => o.subOptions !== undefined && o.subOptions.length > 0);

  const onSubmenuOpen = useCallback(
    (params: {
      anchorRect: DOMRect;
      subOptions: OptionProps[];
      onSelect: (value: string | number | boolean | null) => void;
    }) => {
      if (submenuCloseTimeoutRef.current) {
        clearTimeout(submenuCloseTimeoutRef.current);
        submenuCloseTimeoutRef.current = null;
      }
      if (onSubmenuOpenProp) {
        onSubmenuOpenProp(params);
      } else {
        setSubmenuState(params);
      }
    },
    [onSubmenuOpenProp],
  );

  const onSubmenuMouseLeaveRow = useCallback(() => {
    submenuCloseTimeoutRef.current = setTimeout(() => {
      submenuCloseTimeoutRef.current = null;
      setSubmenuState(null);
    }, SUBMENU_LEAVE_CLOSE_DELAY_MS);
  }, []);

  useEffect(() => {
    if (!submenuState) {
      return;
    }
    const onBlur = () => setSubmenuState(null);
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [submenuState]);

  const getBoundary = (boundary: "left" | "right") => () => {
    if (withinFlyout) {
      return getElementBoundaryById(STUDIO_FLYOUT_MENU_ID, boundary);
    }
    return getElementBoundaryById(StudioBrowserTabs.INSPECTOR, boundary);
  };

  return (
    <div ref={ref} className={cx({ styles, inspectorSelect: true, disabled })}>
      {label && <span className={labelClassName}>{label}</span>}
      <ExpandableSelect
        disabled={disabled}
        value={value}
        onChange={(value) => onChange?.(value as string)}
        options={options ?? []}
        labelFromValue={rename}
        selectSize="medium"
        placeholder={placeholder}
        openImmediately={openImmediately}
        clearOnChange={clearOnChange}
        persistOnChange={persistOnChange}
        additiveOption={additiveOption}
        getLeftOptionsBoundary={getBoundary("left")}
        getRightOptionsBoundary={getBoundary("right")}
        optionsAlignment="right"
        showSelectedOptionCheck
        mouseEvent={mouseEvent}
        className={className}
        hideInput={hideInput}
        inputFormatter={inputFormatter}
        optionsClassName={optionsClassName}
        showTooltip={showTooltip}
        tooltipText={tooltipText}
        tooltipDelay={tooltipDelay}
        onSubmenuOpen={hasSubmenuOptions ? onSubmenuOpen : undefined}
        onSubmenuMouseLeaveRow={hasSubmenuOptions ? onSubmenuMouseLeaveRow : undefined}
        onClose={hasSubmenuOptions ? () => setSubmenuState(null) : undefined}
      />
      {submenuState && (
        <ContextMenu
          open
          setOpen={(_, open) => {
            if (!open) {
              setSubmenuState(null);
            }
          }}
          x={submenuState.anchorRect.left}
          y={submenuState.anchorRect.top}
          alignRightToX={submenuState.anchorRect.left}
          closeOnClick
          onMouseEnter={() => {
            if (submenuCloseTimeoutRef.current) {
              clearTimeout(submenuCloseTimeoutRef.current);
              submenuCloseTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => setSubmenuState(null)}
        >
          {submenuState.subOptions.map((sub, j) => (
            <SubmenuInnerButton
              key={`${sub.value}-${j}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                submenuState.onSelect(sub.value);
                setSubmenuState(null);
              }}
            >
              {sub.label}
            </SubmenuInnerButton>
          ))}
        </ContextMenu>
      )}
    </div>
  );
};

export type TextInputSectionProps = { title: string; bold?: boolean } & ReturnCommitTextInputProps;

export type NumberInputSectionProps = { title: string; bold?: boolean } & ReturnCommitNumberInputProps;

export type TextareaInputSectionProps = {
  title: string;
  bold?: boolean;
  disabled?: boolean;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
};

export interface CheckboxInputSectionProps {
  title: string;
  bold?: boolean;
  disabled?: boolean;
  value: boolean;
  onChange: (s: boolean) => void;
  id?: string;
}

export interface RangeInputSectionProps {
  title: string;
  bold?: boolean;
  disabled?: boolean;
  lowerBound: number;
  upperBound: number;
  step?: number;
  value: number;
  onChange: (v: number) => void;
}

export interface SelectSectionProps extends InspectorSelectProps<string | number> {
  title: string;
  validationError?: string;
  bold?: boolean;
  style?: CSSProperties;
  renamer?: (x: string) => string | undefined;
  fix?: () => void;
}

interface InspectorIconButtonGroupProps {
  group: {
    icon: ReactNode;
    onClick: (e: React.MouseEvent) => void;
    disabled?: boolean;
    title?: string;
  }[];
}

export function InspectorIconButtonGroup({ group }: InspectorIconButtonGroupProps) {
  return (
    <div className={styles.inspectorIconButtonGroupWrapper}>
      {group.map(({ icon, onClick, disabled, title }, i) => (
        <button type="button" key={i} onClick={onClick} disabled={disabled} title={title}>
          {icon}
        </button>
      ))}
    </div>
  );
}

interface ExternalEntityLinkProps {
  href: string;
  label: string;
  icon?: ReactNode;
}

export function ExternalEntityLink({ href, label, icon = null }: ExternalEntityLinkProps) {
  return (
    <div className={styles.externalEntityLinkWrapper}>
      <Link href={href}>
        <div className={styles.externalEntityLinkLabelWrapper}>
          {icon}
          <span>{label}</span>
        </div>
        <IconLineArrow size={11} />
      </Link>
    </div>
  );
}

export function InfoTooltip({ children }: { children: ReactNode }) {
  return (
    <StudioTooltip delayDuration={200} maxContentWidth={400} content={children}>
      <div className={styles.infoWrapper}>
        <IconQuestionMark />
      </div>
    </StudioTooltip>
  );
}
